import { cn } from '@nextui-org/react'
import { GoTrash } from '@react-icons/all-files/go/GoTrash'
import { PiMicrosoftExcelLogoLight } from '@react-icons/all-files/pi/PiMicrosoftExcelLogoLight'
import React, { useCallback, useEffect, useState } from 'react'
import XLSX from 'xlsx'
import { FileUploadIcon } from '~/constants/icon'

interface FileUploadProps {
  onFileChange: (file: File | null) => void
  withExamplePrefilled?: boolean
  className?: string
}

export default function FileUpload({
  onFileChange,
  withExamplePrefilled = false,
  className
}: FileUploadProps) {
  const EXAMPLE_FILE_NAME = 'MappingTableFormat1.xlsx'
  const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const [dragOver, setDragOver] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  const handleFileUpload = useCallback(
    (file: File | null) => {
      setFile(file)
      onFileChange?.(file)
    },
    [onFileChange]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (!withExamplePrefilled) {
        setDragOver(true)
      }
    },
    [withExamplePrefilled]
  )

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (withExamplePrefilled) return

      setDragOver(false)
      const files = e.dataTransfer.files
      if (files[0]?.type.includes('sheet')) {
        handleFileUpload(files[0])
      }
    },
    [withExamplePrefilled, handleFileUpload]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (withExamplePrefilled) return

      const files = e.target.files
      if (files) {
        handleFileUpload(files[0])
      }
    },
    [withExamplePrefilled, handleFileUpload]
  )

  const loadExampleXlsx = useCallback(async () => {
    const createBuiltInExampleFile = () => {
      const rows = [
        {
          'Source Field': '$.customer.id',
          'Target Field': '$.customerId',
          Transform: 'direct mapping',
          Example: '12345'
        },
        {
          'Source Field': '$.customer.email',
          'Target Field': '$.contact.email',
          Transform: 'lowercase',
          Example: 'USER@EXAMPLE.COM -> user@example.com'
        },
        {
          'Source Field': '$.order.total',
          'Target Field': '$.amount',
          Transform: 'number',
          Example: '250.75'
        }
      ]
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'MappingTable')
      const workbookArray = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([workbookArray], { type: XLSX_MIME })
      return new File([blob], EXAMPLE_FILE_NAME, { type: XLSX_MIME })
    }

    try {
      setLoading(true)
      const response = await fetch('/examples/MappingTableFormat1.xlsx')
      if (!response.ok) {
        throw new Error('Failed to fetch example XLSX')
      }

      const blob = await response.blob()
      const responseContentType = response.headers?.get?.('content-type')?.toLowerCase() || ''
      const blobContentType = blob.type?.toLowerCase() || ''
      const combinedContentType = `${responseContentType};${blobContentType}`

      // Protect against SPA fallback HTML being fetched as an ".xlsx" file.
      if (combinedContentType.includes('text/html')) {
        throw new Error('Example XLSX path resolved to HTML content')
      }

      handleFileUpload(new File([blob], EXAMPLE_FILE_NAME, { type: XLSX_MIME }))
    } catch (error) {
      // Fallback to a built-in generated XLSX example, so "Try Example" always works.
      console.warn('Falling back to built-in mapping table example XLSX:', error)
      handleFileUpload(createBuiltInExampleFile())
    } finally {
      setLoading(false)
    }
  }, [handleFileUpload])

  useEffect(() => {
    if (withExamplePrefilled) {
      loadExampleXlsx()
    }
  }, [withExamplePrefilled, loadExampleXlsx])

  if (loading) {
    return (
      <div className={cn('w-full flex items-center justify-between mb-4', className)}>
        <div
          className={cn(
            'border border-[#d4dce2] rounded-2xl flex items-center min-w-[250px] flex-1',
            'overflow-hidden text-ellipsis'
          )}
        >
          <div className='file-type h-full border-r-1 border-[#d4dce2] py-2 px-5'>
            <PiMicrosoftExcelLogoLight size={32} color='#6f7987' />
          </div>
          <div
            className={cn(
              'ml-2 whitespace-nowrap text-sm text-default-700 max-w-[70%]',
              'overflow-hidden text-ellipsis'
            )}
          >
            Loading example file...
          </div>
        </div>
      </div>
    )
  }

  if (file) {
    return (
      <div className={cn('w-full flex items-center justify-between mb-4', className)}>
        <div
          className={cn(
            'border border-[#d4dce2] rounded-2xl flex items-center min-w-[250px] flex-1',
            'overflow-hidden text-ellipsis'
          )}
        >
          <div className='file-type h-full border-r-1 border-[#d4dce2] py-2 px-5'>
            <PiMicrosoftExcelLogoLight size={32} color='#6f7987' />
          </div>
          <div
            className={cn(
              'ml-2 whitespace-nowrap text-sm text-default-700 max-w-[70%]',
              'overflow-hidden text-ellipsis'
            )}
          >
            {file.name}
          </div>
        </div>
        {!withExamplePrefilled && (
          <div
            className={cn(
              'button-submit bg-white text-default-600',
              'px-[10px] rounded-2xl cursor-pointer',
              'border border-[#d4dce2] select-none h-8 hover:bg-light3',
              'disabled:cursor-not-allowed',
              'w-12 h-12 ml-2 flex items-center justify-center'
            )}
            onClick={() => handleFileUpload(null)}
          >
            <GoTrash size={16} className='w-[16px] h-[16px]' />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'upload-container my-2 h-[140px] w-full border-2 border-dashed rounded-2xl',
        dragOver ? 'border-[#6f7987] bg-[#e8effb]' : 'border-[#d4dce2]',
        'flex items-center justify-center mb-4',
        withExamplePrefilled ? 'opacity-50 cursor-not-allowed' : '',
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type='file'
        id='fileUpload'
        className='hidden'
        onChange={handleFileChange}
        accept='.xlsx'
        disabled={withExamplePrefilled}
      />

      <label
        htmlFor='fileUpload'
        className={cn(
          'upload-inner w-[80%] flex flex-col items-center justify-center',
          withExamplePrefilled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <FileUploadIcon />
        <div className='description text-sm text-default-600 mt-2'>
          {withExamplePrefilled ? (
            'Loading example file...'
          ) : (
            <>
              Drag and drop or <span className='text-link'>select</span> your Excel file
            </>
          )}
        </div>
        <div className='file-types text-xs text-default-600 mt-2'>XLSX only</div>
      </label>
    </div>
  )
}
